import { ConfirmDialog } from '@wundo/ui';
import type { SendableNotificationChannel } from '@wundo/shared/notificationChannel.ts';
import type {
  NotificationMeeting,
  NotificationRecipient,
  NotificationSkipReason,
} from '@/lib/notificationSend.ts';
import { nameOf, skipLabel, titleOf } from './sendLabels.ts';

const CHANNEL_PHRASES: Record<SendableNotificationChannel, string> = {
  msteams: 'in Teams',
  slack: 'in Slack',
  email: 'by email',
};

interface SendConfirmDialogProps {
  open: boolean;
  error: string;
  kindLabel: string;
  isUserScope: boolean;
  isEmail: boolean;
  channel: SendableNotificationChannel | undefined;
  resend: boolean;
  takesRoleplay: boolean;
  reachableCount: number;
  /** Every ticked person on the list, reachable or not. */
  selectedCount: number;
  leftOutReasons: NotificationSkipReason[];
  resentCount: number;
  recipient: NotificationRecipient | null;
  meeting: NotificationMeeting | undefined;
  /** Whether the recipient already received this meeting moment. */
  settled: boolean | undefined;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

/* "has no Teams chat with Wundo (2); already did this roleplay (1)", most common first. */
function leftOutSummary(reasons: NotificationSkipReason[]): string {
  const counts = new Map<NotificationSkipReason, number>();
  for (const reason of reasons) counts.set(reason, (counts.get(reason) ?? 0) + 1);

  return [...counts]
    .sort((a, b) => b[1] - a[1])
    .map(([reason, count]) => `${skipLabel(reason)} (${count})`)
    .join('; ');
}

function channelPhrase({ isEmail, channel }: SendConfirmDialogProps): string {
  if (isEmail) return ` ${CHANNEL_PHRASES.email}`;
  return channel ? ` ${CHANNEL_PHRASES[channel]}` : '';
}

function resendNote({
  isEmail,
  resend,
  resentCount,
  takesRoleplay,
}: SendConfirmDialogProps): string | null {
  if (isEmail) {
    return 'Sending again to someone who already received it is allowed; the history keeps both.';
  }
  if (resend) {
    return resentCount > 0
      ? `${resentCount} of them already received it and ${resentCount === 1 ? 'gets' : 'get'} it again.`
      : null;
  }
  return takesRoleplay
    ? 'Each of them receives it once for this roleplay.'
    : 'Each of them receives it once, ever.';
}

function listDescription(props: SendConfirmDialogProps): string {
  const { kindLabel, reachableCount, selectedCount, leftOutReasons } = props;
  const leftOutCount = leftOutReasons.length;

  return [
    `${kindLabel} goes to ${reachableCount} ${reachableCount === 1 ? 'person' : 'people'} now${channelPhrase(props)}.`,
    leftOutCount > 0
      ? `${leftOutCount} of the ${selectedCount} you ticked ${leftOutCount === 1 ? 'is' : 'are'} left out: ${leftOutSummary(leftOutReasons)}.`
      : null,
    resendNote(props),
  ]
    .filter(Boolean)
    .join(' ');
}

function meetingDescription(props: SendConfirmDialogProps): string {
  const { kindLabel, recipient, meeting, resend, settled } = props;

  return [
    `${kindLabel} goes to ${recipient ? nameOf(recipient) : ''}${channelPhrase(props)} about '${meeting ? titleOf(meeting) : ''}' right now.`,
    resend && settled
      ? 'They already received it for this meeting and get it again.'
      : null,
    'This settles the moment: it will never fire automatically afterwards.',
  ]
    .filter(Boolean)
    .join(' ');
}

export function SendConfirmDialog(props: SendConfirmDialogProps) {
  const { open, error, isEmail, isUserScope, onClose, onConfirm } = props;

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      title={isEmail ? 'Send this email?' : 'Send this notification?'}
      description={isUserScope ? listDescription(props) : meetingDescription(props)}
      confirmLabel='Send now'
      cancelLabel='Cancel'
      error={error}
      onConfirm={onConfirm}
    />
  );
}
